/* Newseze AdSlot — when no real ad network is configured, render a clean
   first-party promo (rotates between Sellpages, Pro tier, Rewards, Daily
   Brief) instead of a "AdSense compatible" debug stub.

   When REACT_APP_ADSENSE_CLIENT is set, real AdSense markup is emitted. */
import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Coins, Crown, Tag, Mail, Sparkles, ArrowRight } from "lucide-react";

const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT || "";

// First-party promo inventory — clean editorial cards we can rotate while
// the real ad networks are pending approval. Picks one deterministically by
// the slot name so different slots show different promos on the same page.
// All copy is "safe" — no third-party tracking, no AdSense compliance debt.
const HOUSE_PROMOS = [
  {
    key: "sellpages",
    tone: "gold",
    icon: Tag,
    eyebrow: "Newseze Sellpages",
    title: "Sell anything to the whole Newseze audience.",
    body: "Unlimited classified listings with AI title polish. Readers earn credits for viewing — more eyeballs, more clicks.",
    cta: "Start selling · $24.95/mo",
    href: "/sellpages",
  },
  {
    key: "pro",
    tone: "blue",
    icon: Crown,
    eyebrow: "Newseze Pro",
    title: "Ad-free Newseze + premium rewards unlocked.",
    body: "Skip every ad, unlock unlimited Boost subjects, and unlock premium reward tiers. $9.99/month.",
    cta: "Go Pro",
    href: "/?go-pro=1",
  },
  {
    key: "rewards",
    tone: "navy",
    icon: Coins,
    eyebrow: "News with Rewards",
    title: "Honest news. That rewards you for reading.",
    body: "Earn credits for every story · gift cards, Pro credit, charity, or $500/mo sweepstakes. Safe by design.",
    cta: "See rewards",
    href: "/rewards",
  },
  {
    key: "benefits",
    tone: "blue",
    icon: Tag,
    eyebrow: "Member Benefits",
    title: "100+ partner deals exclusive to Newseze members.",
    body: "Insurance, credit, travel, learning, software — saved-up subscriber benefits across 15 categories.",
    cta: "Browse benefits",
    href: "/benefits",
  },
  {
    key: "brief",
    tone: "sky",
    icon: Mail,
    eyebrow: "Newseze Daily Brief",
    title: "Five balanced stories. One email. Every morning.",
    body: "Free, calm, and respectful. No clickbait, no doomscroll. The brief 60,000 Americans already read.",
    cta: "Subscribe free",
    href: "/?brief=1",
  },
  {
    key: "extension",
    tone: "gold",
    icon: Sparkles,
    eyebrow: "Newseze Anywhere",
    title: "Earn credits on every news site you read.",
    body: "Drag our bookmarklet to your bookmarks bar — Newseze rides along on Fox, CNN, NYT, anywhere.",
    cta: "Install free",
    href: "/extension",
  },
];

function pickPromo(slot) {
  // Pin specific slots to specific promos so the Benefits + Pro + Rewards
  // promos always surface on the home page, regardless of hash. Other slots
  // fall back to a deterministic hash over the inventory.
  const PINNED = {
    "headlines-hero-premium": "benefits",
    "homepage-mid-banner": "rewards",
    "homepage-low-banner": "rewards",
    "homepage-top": "pro",
  };
  if (PINNED[slot]) {
    const pinned = HOUSE_PROMOS.find((p) => p.key === PINNED[slot]);
    if (pinned) return pinned;
  }
  // Stable hash of slot → promo index (different slots = different promos)
  let h = 0;
  const s = String(slot);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return HOUSE_PROMOS[h % HOUSE_PROMOS.length];
}

const TONES = {
  gold: { bg: "linear-gradient(135deg,#fff7d6 0%,#fde68a 100%)", ink: "#1d1303", accent: "#7a5b00", btn: "bg-[#1d1303] text-[var(--gold)]" },
  blue: { bg: "linear-gradient(135deg,#0a1f3d 0%,#1e40af 100%)", ink: "#ffffff", accent: "var(--gold)", btn: "bg-[var(--gold)] text-[#1d1303]" },
  navy: { bg: "linear-gradient(135deg,#0a1f3d 0%,#13294b 100%)", ink: "#ffffff", accent: "var(--gold)", btn: "bg-[var(--gold)] text-[#1d1303]" },
  sky: { bg: "linear-gradient(135deg,#dbeafe 0%,#bfdbfe 100%)", ink: "#0a1f3d", accent: "#1e40af", btn: "bg-[#0a1f3d] text-white" },
};

export default function AdSlot({ size = "h-rect", slot = "homepage-top", label = "Sponsored", earn = 5, testId }) {
  const pushedRef = useRef(false);
  const tid = testId || `ad-slot-${slot}`;
  useEffect(() => {
    if (ADSENSE_CLIENT && typeof window !== "undefined" && window.adsbygoogle && !pushedRef.current) {
      try { window.adsbygoogle.push({}); pushedRef.current = true; } catch (e) {}
    }
  }, []);

  const promo = useMemo(() => pickPromo(slot), [slot]);
  const tone = TONES[promo.tone];
  const Icon = promo.icon;

  const earnBadge = earn > 0 ? (
    <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] font-black text-[var(--gold)] bg-[#1d1303] px-1.5 py-0.5 rounded" data-testid={`${tid}-earn-badge`}>
      <Coins size={9} /> +{earn} credits when viewed
    </span>
  ) : null;

  if (ADSENSE_CLIENT) {
    return (
      <div className={`ad-slot ${size}`} data-slot={slot} data-testid={tid}>
        <div className="flex items-center justify-between mb-1">
          <small>{label}</small>
          {earnBadge}
        </div>
        <ins
          className="adsbygoogle"
          style={{ display: "block", width: "100%", height: "100%" }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={slot}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      </div>
    );
  }

  // Newseze first-party promo (replaces the tacky "AdSense compatible" stub)
  return (
    <div className={`ad-slot ${size} !p-0 !border-0 overflow-hidden rounded-xl`} data-slot={slot} data-testid={tid}>
      <Link to={promo.href} className="block h-full w-full" style={{ background: tone.bg, color: tone.ink }} data-testid={`${tid}-promo`}>
        <div className="px-4 py-3 flex flex-col h-full justify-between gap-2">
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] font-black" style={{ color: tone.accent }}>
              <Icon size={11} /> {promo.eyebrow}
            </span>
            <span className="text-[9px] uppercase tracking-[0.14em] font-black opacity-70">{label}</span>
          </div>
          <div>
            <div className="text-[15px] sm:text-[16px] font-black tracking-[-0.02em] leading-snug">{promo.title}</div>
            <p className="text-[11.5px] mt-1 leading-snug opacity-90 line-clamp-2">{promo.body}</p>
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-black px-2.5 py-1 rounded-full ${tone.btn}`} data-testid={`${tid}-cta`}>
              {promo.cta} <ArrowRight size={10} />
            </span>
            {earnBadge}
          </div>
        </div>
      </Link>
    </div>
  );
}
